import { FC } from 'react';
import {
  IncentiveOffer,
  IncentiveOfferPlanClass,
  IncentiveOfferStatus,
  IncentivesMarketBeneficiary,
} from 'modules/incentivesMarket/types';

import {
  formatCurrencyDecimals,
  formatNumber,
} from 'utils/formats';
import { useTranslate } from 'hooks/useTranslate';
import { isPlanClassVisible } from 'modules/incentivesMarket/utils/isPlanClassVisible';
import IncentiveOfferStatusBadge from '../../../components/IncentiveOfferStatusBadge';

type IncentivesMarketAdminPanelOfferItemProps = {
  offer: IncentiveOffer;
  isSell: boolean;
  onViewDetail: (id: string) => void; // eslint-disable-line no-unused-vars
  getOfferStatus: (o: IncentiveOffer) => IncentiveOfferStatus; // eslint-disable-line no-unused-vars
  getBeneficiaryName: (p: string | Partial<IncentivesMarketBeneficiary>) => string; // eslint-disable-line no-unused-vars
  getShareClassName: (s: string | Partial<IncentiveOfferPlanClass> | null) => string; // eslint-disable-line no-unused-vars
};

const IncentivesMarketAdminPanelOfferItem: FC<
  IncentivesMarketAdminPanelOfferItemProps
> = ({
  offer,
  isSell,
  onViewDetail,
  getOfferStatus,
  getBeneficiaryName,
  getShareClassName,
}) => {
  const { t } = useTranslate();

  const status = getOfferStatus(offer);
  const partnerName = getBeneficiaryName(offer.beneficiary);
  const unitClassName = getShareClassName(offer.unitClass);
  const totalAmount = offer.unitsCount.total * offer.unitPrice;
  const iconBg = isSell
    ? 'bg-info-dim text-info'
    : 'bg-info-dim text-info';
  const iconName = isSell ? 'ni-wallet-out' : 'ni-wallet-in';

  const formatOfferDescription = () => {
    const priceBlock = `${formatCurrencyDecimals(offer.unitPrice)}/${t(
      'IncentivesMarketUnit'
    ).toLowerCase()}`;
    const totalBlock = `${t('Total')}: ${formatCurrencyDecimals(totalAmount)}`;
    const partnerBlock = `${isSell ? t('Seller') : t('Buyer')}: ${partnerName}`;

    return `${priceBlock} · ${totalBlock} · ${partnerBlock}`;
  };

  const formatOfferSummary = () => {
    const sharesBlock = `${formatNumber(offer.unitsCount.total)} ${t(
      'IncentivesMarketUnits'
    ).toLowerCase()}`;
    const shareClassNameBlock = isPlanClassVisible()
      ? `· ${unitClassName}`
      : '';

    return `${sharesBlock} ${shareClassNameBlock}`;
  };

  return (
    <div
      className="col-12 list-item-oferta doc-item d-flex align-items-center cursor-pointer"
      onClick={() => onViewDetail(offer._id)}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => e.key === 'Enter' && onViewDetail(offer._id)}
    >
      <div className={`doc-icon ${iconBg}`}>
        <em className={`icon ni ${iconName}`} />
      </div>

      <div className="flex-grow-1">
        <strong className="fs-13px">{formatOfferSummary()}</strong>
        <div className="text-soft fs-12px">{formatOfferDescription()}</div>
      </div>

      <div className="d-flex align-items-center gap-2">
        <IncentiveOfferStatusBadge status={status} />

        <a
          className="btn btn-icon btn-round btn-trans"
          href="#"
          title={t('ViewDetail')}
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            onViewDetail(offer._id);
          }}
        >
          <em className="icon ni ni-forward-ios" />
        </a>
      </div>
    </div>
  );
};

export default IncentivesMarketAdminPanelOfferItem;
